import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, Loader,
  Share2, Download, ExternalLink, Globe, Lock, Unlock,
  Zap, X, Star
} from 'lucide-react';
import ModelLogo from './ModelLogo';
import { showToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// Detect RTL text
const isRTL = (text) => {
  if (!text) return false;
  const rtlChars = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/;
  return rtlChars.test(text.substring(0, 100));
};

// Format relative time
const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Social Icons
const SocialIcons = {
  WhatsApp: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#25D366">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  ),
  Facebook: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  Twitter: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#000000">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  Telegram: () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#0088cc">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
    </svg>
  )
};

// Parse and render rich text (bold, italic)
function renderRichText(text) {
  if (!text) return null;

  // First handle **bold**, then *italic*
  const result = [];
  let key = 0;

  // Split by **bold** pattern first
  const boldParts = text.split(/(\*\*[^*]+\*\*)/g);

  boldParts.forEach((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Bold text
      const boldContent = part.slice(2, -2);
      result.push(
        <strong key={key++} className="font-bold text-gray-900">{boldContent}</strong>
      );
    } else if (part.includes('*')) {
      // Check for *italic* within this part
      const italicParts = part.split(/(\*[^*]+\*)/g);
      italicParts.forEach((iPart) => {
        if (iPart.startsWith('*') && iPart.endsWith('*') && iPart.length > 2) {
          result.push(
            <em key={key++} className="italic">{iPart.slice(1, -1)}</em>
          );
        } else if (iPart) {
          result.push(iPart);
        }
      });
    } else if (part) {
      result.push(part);
    }
  });

  return result.length > 0 ? result : text;
}

// Chat Message Component
function ChatMessage({ message, isAlternate = false }) {
  const textDirection = isRTL(message.content) ? 'rtl' : 'ltr';
  const textAlign = isRTL(message.content) ? 'text-right' : 'text-left';

  const bgColor = isAlternate ? 'bg-blue-50 border-blue-100' : 'bg-white border-gray-200';
  const alignment = isAlternate ? 'ml-4 sm:ml-12' : 'mr-4 sm:mr-12';

  return (
    <div className={`${alignment} mb-4`}>
      <div className={`flex items-center gap-2 mb-1.5 ${isAlternate ? 'justify-end' : ''}`}>
        {!isAlternate && <ModelLogo modelName={message.name} size="sm" />}
        <span className="text-sm font-semibold text-gray-800">{message.name}</span>
        <span className="text-xs text-gray-400">{message.latency_ms ? `${(message.latency_ms / 1000).toFixed(1)}s` : ''}</span>
        {isAlternate && <ModelLogo modelName={message.name} size="sm" />}
      </div>

      <div className={`${bgColor} rounded-2xl px-4 py-3 shadow-sm border`} dir={textDirection}>
        <p className={`text-base text-gray-700 whitespace-pre-wrap leading-relaxed ${textAlign}`}>
          {renderRichText(message.content)}
        </p>
      </div>

      <div className={`flex items-center gap-2 mt-1.5 text-xs text-gray-400 ${isAlternate ? 'justify-end' : ''}`}>
        <span>{message.tokens} tokens</span>
        <span>•</span>
        <span>{message.cost?.toFixed(2)} cr</span>
      </div>
    </div>
  );
}

// Stats Panel Component
function StatsPanel({ stats }) {
  if (!stats) return null;

  const modelsList = Object.entries(stats.per_model || {});

  const fastestModel = modelsList.reduce((best, [id, data]) => {
    if (!best || (data.avg_latency_ms && data.avg_latency_ms < best.latency)) {
      return { id, latency: data.avg_latency_ms };
    }
    return best;
  }, null)?.id;

  const cheapestModel = modelsList.reduce((best, [id, data]) => {
    if (!best || data.cost < best.cost) {
      return { id, cost: data.cost };
    }
    return best;
  }, null)?.id;

  const getCellStyle = (isBest) => isBest ? 'bg-blue-100 text-blue-700' : 'text-gray-600';

  const formatLatency = (ms) => {
    if (!ms) return '-';
    if (ms >= 1000) return (ms / 1000).toFixed(1) + 's';
    return ms + 'ms';
  };

  const formatCost = (cost) => {
    if (!cost && cost !== 0) return '-';
    if (cost < 0.01) return '<0.01 cr';
    return cost.toFixed(2) + ' cr';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
      <div className="px-4 py-3 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="12" width="4" height="8" rx="1" fill="#10B981" />
              <rect x="10" y="6" width="4" height="14" rx="1" fill="#F472B6" />
              <rect x="16" y="2" width="4" height="18" rx="1" fill="#60A5FA" />
            </svg>
            Statistics
          </h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">{stats.total_messages} msgs</span>
            <span className="text-gray-500">{Math.round(stats.duration_seconds)}s</span>
            <span className="font-semibold text-blue-600">{formatCost(stats.total_cost)}</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="py-2 px-4 text-left font-semibold text-gray-600">Model</th>
              <th className="py-2 px-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={14} className="text-amber-500" />
                  <span className="text-xs text-gray-500">SPEED</span>
                </div>
              </th>
              <th className="py-2 px-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <Zap size={14} className="text-yellow-500" />
                  <span className="text-xs text-gray-500">CREDITS</span>
                </div>
              </th>
              <th className="py-2 px-3 text-center">
                <div className="flex items-center justify-center gap-1">
                  <MessageCircle size={14} className="text-blue-500" />
                  <span className="text-xs text-gray-500">MSGS</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {modelsList.map(([modelId, data], index) => (
              <tr key={modelId} className={`border-b border-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <ModelLogo modelName={data.name} size="xs" />
                    <span className="font-medium text-gray-700">{data.name}</span>
                  </div>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={`font-bold px-2 py-0.5 rounded ${getCellStyle(modelId === fastestModel)}`}>
                    {formatLatency(data.avg_latency_ms)}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={`font-bold px-2 py-0.5 rounded ${getCellStyle(modelId === cheapestModel)}`}>
                    {formatCost(data.cost)}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className="font-bold text-gray-600">{data.messages}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transparency note */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-xs text-gray-400 text-center">
          ⚖️ All models received the exact same prompt and rules. No custom tuning.
        </p>
      </div>
    </div>
  );
}

// Main ChatDetail Component
function ChatDetail({ user }) {
  const { chatId } = useParams();
  const chatContainerRef = useRef(null);
  const shareMenuRef = useRef(null);

  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [videoGenerating, setVideoGenerating] = useState(false);

  // Close share menu on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle vote
  const handleVote = async () => {
    if (votingInProgress) return;

    const token = localStorage.getItem('token');
    if (!token) {
      showToast('Please log in to vote', 'info');
      return;
    }

    setVotingInProgress(true);
    const wasVoted = chat.user_voted;

    // Optimistic update
    setChat(prev => ({
      ...prev,
      votes: wasVoted ? (prev.votes || 1) - 1 : (prev.votes || 0) + 1,
      user_voted: !wasVoted
    }));

    try {
      const response = await fetch(`${API_URL}/group-chat/vote/${chatId}`, {
        method: wasVoted ? 'DELETE' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setChat(prev => ({
          ...prev,
          votes: data.votes,
          user_voted: data.user_voted
        }));
      } else {
        // Revert on error
        setChat(prev => ({
          ...prev,
          votes: wasVoted ? (prev.votes || 0) + 1 : (prev.votes || 1) - 1,
          user_voted: wasVoted
        }));
        showToast('Failed to vote', 'error');
      }
    } catch (err) {
      // Revert on error
      setChat(prev => ({
        ...prev,
        votes: wasVoted ? (prev.votes || 0) + 1 : (prev.votes || 1) - 1,
        user_voted: wasVoted
      }));
      showToast('Failed to vote', 'error');
    } finally {
      setVotingInProgress(false);
    }
  };

  useEffect(() => {
    loadChat();
  }, [chatId]);

  const loadChat = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      // STATIC MODE: load from local JSON file and find by chatId
      const response = await fetch('/data/chats.json');
      if (!response.ok) { setError('Failed to load chat'); return; }
      const allData = await response.json();
      const allChats = allData.chats || [];
      const data = allChats.find(c => c.id === chatId);
      if (!data) { setError('Chat not found'); return; }
      setChat(data);
    } catch (err) {
      console.error('Failed to load chat:', err);
      setError('Failed to load chat');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePublic = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showToast('Please login to change privacy', 'error');
        return;
      }

      const response = await fetch(`${API_URL}/group-chat/view/${chatId}/toggle-public`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setChat(prev => ({ ...prev, is_public: data.is_public }));
        showToast(data.is_public ? 'Chat is now public' : 'Chat is now private', 'success');
      }
    } catch (err) {
      showToast('Failed to update privacy', 'error');
    }
  };

  const shareUrl = `${window.location.origin}/chat/${chatId}`;
  const shareText = `Check out this AI group chat on MidoLABS! 🤖`;

  const openInNewTab = () => {
    window.open(shareUrl, '_blank');
  };

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, '_blank');
    setShowShareMenu(false);
  };

  // Download as video - Pro+ only
  const handleDownloadVideo = async () => {
    if (videoGenerating) return;

    setVideoGenerating(true);
    showToast('🎬 Generating video... This may take a minute', 'info');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/group-chat/view/${chatId}/export-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `chat-${chatId}.mp4`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        showToast('✅ Video downloaded!', 'success');
      } else {
        const data = await response.json();
        showToast(data.detail || 'Failed to generate video', 'error');
      }
    } catch (err) {
      console.error('Video generation error:', err);
      showToast('Failed to generate video', 'error');
    } finally {
      setVideoGenerating(false);
    }
  };

  const handleDownloadFile = () => {
    if (!chat?.messages?.length) {
      showToast('No messages to export', 'error');
      return;
    }

    // Create markdown content
    let content = `# ${chat.topic}\n\n`;
    content += `**Models:** ${chat.models?.map(m => m.name || m).join(', ')}\n`;
    content += `**Date:** ${new Date(chat.created_at).toLocaleDateString()}\n`;
    content += `**Messages:** ${chat.total_messages}\n\n`;
    content += `---\n\n`;

    // Add messages
    chat.messages.forEach((msg, i) => {
      content += `### ${msg.name}\n`;
      content += `${msg.content}\n\n`;
      content += `*${msg.tokens} tokens • ${msg.cost?.toFixed(2)} cr • ${(msg.latency_ms / 1000).toFixed(1)}s*\n\n`;
    });

    // Add stats
    if (chat.per_model_stats) {
      content += `---\n\n## Statistics\n\n`;
      content += `| Model | Avg Speed | Credits | Messages |\n`;
      content += `|-------|-----------|---------|----------|\n`;
      Object.entries(chat.per_model_stats).forEach(([id, data]) => {
        const speed = data.avg_latency_ms ? `${(data.avg_latency_ms / 1000).toFixed(1)}s` : '-';
        const cost = data.cost ? `${data.cost.toFixed(2)} cr` : '-';
        content += `| ${data.name} | ${speed} | ${cost} | ${data.messages} |\n`;
      });
    }

    // Download file
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = `chat-${chatId}.md`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);

    showToast('Chat exported!', 'success');
  };

  // Loading state
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-20">
          <Loader className="animate-spin text-blue-500" size={40} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link
          to="/group-chat"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={18} />
          Back to Chats
        </Link>

        <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
          <MessageCircle size={48} className="mx-auto text-gray-300 mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{error}</h2>
          <p className="text-gray-500">The chat may have been deleted or is private.</p>
        </div>
      </div>
    );
  }

  if (!chat) return null;

  const models = chat.models || [];

  return (
    <div className="px-4 sm:px-0 py-6">
    <div className="max-w-4xl mx-auto px-0 sm:px-6">
      {/* Back button */}
      <Link
        to="/group-chat"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={18} />
        Back to Chats
      </Link>

      {/* Chat Header - White background */}
      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200 mb-4">
        {/* Topic */}
        <h1 className="font-bold text-xl sm:text-2xl text-gray-900 mb-4" dir={isRTL(chat.topic) ? 'rtl' : 'ltr'}>
          {chat.topic}
        </h1>

        {/* Models with logos - clear visibility */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {models.map((model, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1.5 border border-gray-200">
              <ModelLogo modelName={model.name || model} size="sm" />
              <span className="text-sm font-medium text-gray-700">{(model.name || model).split(' ')[0]}</span>
            </div>
          ))}
        </div>

        {/* Meta info */}
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
          <span>by {chat.user}</span>
          <span>•</span>
          <span>{formatTimeAgo(chat.created_at)}</span>
          <span>•</span>
          <span>{chat.total_messages} messages</span>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-gray-100">
          {/* Vote button */}
          <button
            onClick={handleVote}
            disabled={votingInProgress}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all ${
              chat.user_voted
                ? 'bg-green-100 text-green-700 font-bold shadow-sm'
                : (chat.votes || 0) > 0
                  ? 'bg-green-50 text-green-600'
                  : 'bg-gray-100 text-gray-500 hover:text-green-500 hover:bg-green-50'
            } ${votingInProgress ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={chat.user_voted ? 'Remove vote' : 'Vote for this debate'}
          >
            <Star size={18} fill={chat.user_voted || (chat.votes || 0) > 0 ? 'currentColor' : 'none'} />
            {(chat.votes || 0) > 0 && (
              <span className="text-sm font-medium">{chat.votes}</span>
            )}
          </button>


          {/* Social Share */}
          <div className="relative" ref={shareMenuRef}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              className="flex items-center gap-1.5 px-3 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors text-blue-700"
            >
              <Share2 size={16} />
              <span className="text-sm font-medium">Share</span>
            </button>

            {showShareMenu && (
              <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
                  <span className="font-semibold text-gray-800 text-sm">Share to</span>
                  <button onClick={() => setShowShareMenu(false)} className="p-1 hover:bg-gray-100 rounded">
                    <X size={14} className="text-gray-500" />
                  </button>
                </div>
                <div className="p-2">
                  <button onClick={shareToWhatsApp} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                    <SocialIcons.WhatsApp />
                    <span className="text-sm font-medium text-gray-700">WhatsApp</span>
                  </button>
                  <button onClick={shareToTwitter} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                    <SocialIcons.Twitter />
                    <span className="text-sm font-medium text-gray-700">X (Twitter)</span>
                  </button>
                  <button onClick={shareToFacebook} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                    <SocialIcons.Facebook />
                    <span className="text-sm font-medium text-gray-700">Facebook</span>
                  </button>
                  <button onClick={shareToTelegram} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 rounded-lg">
                    <SocialIcons.Telegram />
                    <span className="text-sm font-medium text-gray-700">Telegram</span>
                  </button>
                </div>
              </div>
            )}
          </div>


          {/* Video download - Pro+ only */}
          {user && ['pro', 'premium', 'admin'].includes(user.plan_tier) ? (
            <button
              onClick={handleDownloadVideo}
              disabled={videoGenerating}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
                videoGenerating
                  ? 'bg-yellow-200 cursor-wait text-yellow-700'
                  : 'bg-pink-100 hover:bg-pink-200 text-pink-700'
              }`}
              title="Download as Video"
            >
              {videoGenerating ? (
                <Loader size={16} className="animate-spin" />
              ) : (
                <Download size={16} />
              )}
              <span className="text-sm font-medium">
                {videoGenerating ? 'Creating...' : 'Video'}
              </span>
            </button>
          ) : (
            <button
              onClick={() => showToast('🎬 Video export is a Pro feature', 'info')}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 rounded-lg opacity-60"
              title="Video export - Pro feature"
            >
              <Download size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-500">Video</span>
              <span className="text-[10px] bg-yellow-400 text-black px-1 rounded font-bold">PRO</span>
            </button>
          )}

          {/* Stats toggle button - colorful icon */}
          <button
            onClick={() => setShowStats(!showStats)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors ${
              showStats
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="4" y="12" width="4" height="8" rx="1" fill="#10B981" />
              <rect x="10" y="6" width="4" height="14" rx="1" fill="#F472B6" />
              <rect x="16" y="2" width="4" height="18" rx="1" fill="#60A5FA" />
            </svg>
            <span className="text-sm font-medium text-gray-700">Stats</span>
          </button>

          {/* Public/Private - matches comparison style */}
          {chat.is_owner && (
            <button
              onClick={handleTogglePublic}
              title={chat.is_public ? 'Make Private' : 'Make Public'}
              className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {chat.is_public ? <Unlock size={16} className="text-green-600" /> : <Lock size={16} className="text-gray-500" />}
            </button>
          )}
        </div>
      </div>

      {/* Stats Panel - collapsible */}
      {showStats && (() => {
        // Calculate stats from messages if per_model_stats is missing
        let statsData = null;

        if (chat.per_model_stats && Object.keys(chat.per_model_stats).length > 0) {
          statsData = {
            total_messages: chat.total_messages,
            total_cost: chat.total_cost,
            total_tokens: chat.total_tokens,
            duration_seconds: chat.duration_seconds,
            per_model: chat.per_model_stats
          };
        } else if (chat.messages && chat.messages.length > 0) {
          // Calculate from messages
          const perModel = {};
          let totalCost = 0;
          let totalTokens = 0;

          chat.messages.forEach(msg => {
            const modelId = msg.model || msg.name;
            if (!perModel[modelId]) {
              perModel[modelId] = {
                name: msg.name,
                messages: 0,
                tokens: 0,
                cost: 0,
                total_latency: 0
              };
            }
            perModel[modelId].messages += 1;
            perModel[modelId].tokens += msg.tokens || 0;
            perModel[modelId].cost += msg.cost || 0;
            perModel[modelId].total_latency += msg.latency_ms || 0;
            totalCost += msg.cost || 0;
            totalTokens += msg.tokens || 0;
          });

          // Calculate averages
          Object.keys(perModel).forEach(id => {
            const m = perModel[id];
            m.avg_latency_ms = m.messages > 0 ? m.total_latency / m.messages : 0;
          });

          statsData = {
            total_messages: chat.messages.length,
            total_cost: totalCost,
            total_tokens: totalTokens,
            duration_seconds: chat.duration_seconds || 0,
            per_model: perModel
          };
        }

        return statsData ? <StatsPanel stats={statsData} /> : null;
      })()}

      {/* Messages */}
      <div
        ref={chatContainerRef}
        className="bg-gray-50 rounded-xl p-4 max-h-[600px] overflow-y-auto border border-gray-200 mt-4"
      >
        {chat.messages?.map((msg, index) => (
          <ChatMessage key={msg.id} message={msg} isAlternate={index % 2 === 1} />
        ))}
      </div>

      {/* Share prompt - show for owner if chat is private */}
      {chat.is_owner && !chat.is_public && (
        <div className="mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Share2 size={20} className="text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Share this debate?</h4>
                <p className="text-sm text-gray-600">Make it public so others can see this AI discussion</p>
              </div>
            </div>
            <button
              onClick={handleTogglePublic}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <Globe size={16} />
              <span>Make Public</span>
            </button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

export default ChatDetail;